// Generated from products/analytics/sdk/pageErrors.ts; run analytics generate:site-runtime.
/** Automatic, bounded page error observation for the visitor analytics SDK. */
import type { ErrorReport } from "./contracts.gen";
export type BrowserErrorReport = ErrorReport;
type BrowserErrorKind = ErrorReport["kind"];
type BrowserErrorFrame = ErrorReport["frames"][number];

/**
 * Injection seams keep the observer deterministic in tests and removable in
 * non-window embedders. Timers are optional because browsers provide them.
 */
export interface PageErrorObserverOptions {
  target?: Window;
  now?: () => number;
  origin?: string;
  setTimeout?: (callback: () => void, delayMs: number) => unknown;
  clearTimeout?: (handle: unknown) => void;
}

const COALESCE_MS = 2_000;
const MAX_REPORTS = 20;
const MAX_FRAMES = 8;
const MAX_MESSAGE_CHARS = 500;
const MAX_OCCURRENCES = 1_000;
const MAX_PATH_CHARS = 300;
const MAX_STACK_CHARS = 8_192;
const MAX_DIAGNOSTIC_INPUT_CHARS = 8_192;
const MAX_URL_CHARS = 8_192;
const MAX_POSITION = 2_147_483_647;

const SAFE_ERROR_NAMES: ReadonlySet<string> = new Set([
  "AggregateError",
  "Error",
  "EvalError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "TypeError",
  "URIError",
  "AbortError",
  "ConstraintError",
  "DataCloneError",
  "DataError",
  "EncodingError",
  "HierarchyRequestError",
  "IndexSizeError",
  "InvalidAccessError",
  "InvalidCharacterError",
  "InvalidModificationError",
  "InvalidNodeTypeError",
  "InvalidStateError",
  "NamespaceError",
  "NetworkError",
  "NoModificationAllowedError",
  "NotFoundError",
  "NotReadableError",
  "NotSupportedError",
  "OperationError",
  "QuotaExceededError",
  "SecurityError",
  "TimeoutError",
  "TransactionInactiveError",
  "URLMismatchError",
  "UnknownError",
  "VersionError",
  "WrongDocumentError",
]);

const SAFE_RESOURCE_TAGS: ReadonlySet<string> = new Set([
  "audio",
  "embed",
  "iframe",
  "img",
  "input",
  "link",
  "object",
  "script",
  "source",
  "track",
  "video",
]);

interface BrowserErrorPayload {
  kind: BrowserErrorKind;
  message: string;
  frames: BrowserErrorFrame[];
}

interface ErrorBucket {
  report: BrowserErrorReport;
  timer: unknown;
  hasTimer: boolean;
}

interface RegisteredListener {
  type: string;
  listener: EventListener;
}

function noop(): void {
  // Intentionally empty cleanup for unsupported targets.
}

function readProperty(target: unknown, key: string): unknown {
  if (!target || (typeof target !== "object" && typeof target !== "function")) return undefined;
  try {
    return (target as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

function normalizeOrigin(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0 || value.length > MAX_URL_CHARS) return null;
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (parsed.username || parsed.password) return null;
    return parsed.origin;
  } catch {
    return null;
  }
}

/**
 * Convert an event source URL to a bounded path on the observer's origin.
 * Query strings, fragments, credentials and cross-origin or non-HTTP URLs are
 * dropped rather than serialized.
 */
function sanitizeSameOriginPath(raw: unknown, origin: string | null | undefined): string | null {
  if (typeof raw !== "string" || raw.length === 0 || raw.length > MAX_URL_CHARS) return null;
  const safeOrigin = normalizeOrigin(origin);
  if (!safeOrigin) return null;
  try {
    const parsed = new URL(raw, `${safeOrigin}/`);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (parsed.origin !== safeOrigin) return null;
    if (parsed.username || parsed.password) return null;
    return parsed.pathname.slice(0, MAX_PATH_CHARS);
  } catch {
    return null;
  }
}

function safePosition(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return 0;
  return Math.min(MAX_POSITION, Math.trunc(value));
}

function frameKey(frame: BrowserErrorFrame): string {
  return JSON.stringify([frame.path, frame.line, frame.column]);
}

function pushFrame(frames: BrowserErrorFrame[], seen: Set<string>, frame: BrowserErrorFrame): void {
  if (frames.length >= MAX_FRAMES) return;
  const key = frameKey(frame);
  if (seen.has(key)) return;
  seen.add(key);
  frames.push(frame);
}

function stackFrames(error: unknown, origin: string | null): BrowserErrorFrame[] {
  const frames: BrowserErrorFrame[] = [];
  const seen = new Set<string>();
  const stack = readProperty(error, "stack");
  if (typeof stack !== "string" || stack.length === 0) return frames;

  const text = stack.slice(0, MAX_STACK_CHARS);
  // Only source-frame lines are parsed, never a rendered error message line.
  const frameLine = /^\s*(?:at\s|(?:[^@\s]+)?@(?=https?:\/\/|\/))/;
  for (const line of text.split("\n")) {
    if (!frameLine.test(line)) continue;
    const pattern = /(?:^|[\s(@])((?:https?:\/\/|\/)[^\s()"']+?):(\d+):(\d+)/g;
    let match = pattern.exec(line);
    while (match && frames.length < MAX_FRAMES) {
      const path = sanitizeSameOriginPath(match[1], origin);
      if (path) {
        pushFrame(frames, seen, {
          path,
          line: safePosition(Number(match[2])),
          column: safePosition(Number(match[3])),
        });
      }
      match = pattern.exec(line);
    }
    if (frames.length >= MAX_FRAMES) break;
  }
  return frames;
}

function runtimeFrames(event: unknown, origin: string | null): BrowserErrorFrame[] {
  const frames: BrowserErrorFrame[] = [];
  const seen = new Set<string>();
  const path = sanitizeSameOriginPath(readProperty(event, "filename"), origin);
  if (path) {
    pushFrame(frames, seen, {
      path,
      line: safePosition(readProperty(event, "lineno")),
      column: safePosition(readProperty(event, "colno")),
    });
  }
  for (const frame of stackFrames(readProperty(event, "error"), origin)) {
    pushFrame(frames, seen, frame);
  }
  return frames;
}

function safeErrorName(value: unknown): string | null {
  const name = readProperty(value, "name");
  return typeof name === "string" && SAFE_ERROR_NAMES.has(name) ? name : null;
}

/**
 * Apply a bounded, best-effort scrub to one diagnostic string. This is not a
 * complete PII classifier: it removes common URL, identity and credential
 * shapes while keeping ordinary failure text readable.
 */
function maskDiagnosticText(value: string): string {
  let text = value
    .slice(0, MAX_DIAGNOSTIC_INPUT_CHARS)
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  // Full URLs (including credentials, queries and fragments) as one unit.
  text = text.replace(/\bhttps?:\/\/[^\s<>"']+/gi, "[url]");
  text = text.replace(/(^|[\s("'=])\/\/[^\s<>"']+/g, "$1[url]");
  text = text.replace(/\bwww\.[^\s<>"']+/gi, "[url]");
  text = text.replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]");

  text = text.replace(
    /\b(authorization|proxy-authorization)\s*:\s*(?:bearer|basic)\s+[A-Za-z0-9._~+/=-]+/gi,
    "$1: [redacted]",
  );
  text = text.replace(/\b(?:bearer|basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi, "[redacted]");
  text = text.replace(
    /\b(set-cookie|cookie)\s*:\s*[^\s,;]+(?:[;,]\s*[^\s,;]+)*/gi,
    "$1: [redacted]",
  );

  // Sensitive key/value assignments, quoted or unquoted.
  text = text.replace(
    /\b(password|passwd|pwd|access[-_]?token|refresh[-_]?token|session[-_]?token|api[-_]?key|client[-_]?secret|token|secret|auth(?:orization)?|cookie|credential)\b(["'`]?)(\s*[:=]\s*)("(?:\\.|[^"\\])*(?:"|$)|'(?:\\.|[^'\\])*(?:'|$)|`[^`]*(?:`|$)|[^\s,;&]+)/gi,
    "$1$2$3[redacted]",
  );

  text = text.replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, "[redacted]");
  text = text.replace(/\b[A-Za-z0-9_+/=-]{24,8192}(?![A-Za-z0-9_+/=-])/g, (candidate) =>
    /\d/.test(candidate) && /[A-Za-z]/.test(candidate) ? "[redacted]" : candidate,
  );

  // Query strings and fragments on relative URLs that survived the passes above.
  text = text.replace(/(\/[^\s?#"'`]+)\?[^\s#"'`]+/g, "$1?[query]");
  text = text.replace(/\?[^\s?#"'`]*=[^\s#"'`]*/g, "?[query]");
  text = text.replace(/(\/[^\s?#"'`]+)#[^\s"'`]+/g, "$1#[fragment]");

  return text.replace(/\s+/g, " ").trim();
}

function errorNameFromText(value: string): string | null {
  const text = value.replace(/^Uncaught\s+(?:\(in promise\)\s+)?/i, "");
  const match = /^(Error|[A-Za-z][A-Za-z0-9]*Error)\s*:/.exec(text);
  const candidate = match?.[1];
  return candidate && SAFE_ERROR_NAMES.has(candidate) ? candidate : null;
}

function stripMessagePrefixes(value: string, name: string | null): string {
  let text = value.trim();
  text = text.replace(/^Uncaught\s+(?:\(in promise\)\s+)?/i, "").trim();
  if (name && text.toLowerCase().startsWith(`${name}:`.toLowerCase())) {
    text = text.slice(name.length + 1).trim();
  }
  if (name && name !== "Error" && text.toLowerCase().startsWith("error:")) {
    text = text.slice("error:".length).trim();
  }
  return text;
}

/**
 * Build a readable bounded message from an optional native error name and a
 * sanitized diagnostic. The generic base survives when no safe detail exists.
 */
function diagnosticMessage(
  base: string,
  name: string | null,
  raw: string | null | undefined,
): string {
  const sanitized = typeof raw === "string" ? maskDiagnosticText(raw) : "";
  const resolvedName = name ?? errorNameFromText(sanitized);
  let detail = stripMessagePrefixes(sanitized, resolvedName);
  const prefix = resolvedName ? `${base}: ${resolvedName}` : base;
  if (!detail) return prefix.slice(0, MAX_MESSAGE_CHARS);
  const available = MAX_MESSAGE_CHARS - prefix.length - 2;
  if (available <= 0) return prefix.slice(0, MAX_MESSAGE_CHARS);
  if (detail.length > available) detail = detail.slice(0, available);
  return `${prefix}: ${detail}`;
}

function summary(base: string, name: string | null): string {
  return (name ? `${base}: ${name}` : base).slice(0, MAX_MESSAGE_CHARS);
}

function resourceTarget(
  target: unknown,
  origin: string | null,
): { tag: string; path: string | null } | null {
  const rawTag = readProperty(target, "tagName");
  if (typeof rawTag !== "string") return null;
  const tag = rawTag.toLowerCase();
  if (!SAFE_RESOURCE_TAGS.has(tag)) return null;
  for (const candidate of [
    readProperty(target, "currentSrc"),
    readProperty(target, "src"),
    readProperty(target, "href"),
  ]) {
    if (typeof candidate !== "string" || candidate.length === 0) continue;
    return { tag, path: sanitizeSameOriginPath(candidate, origin) };
  }
  return { tag, path: null };
}

function defaultTarget(): Window | null {
  try {
    return typeof window === "undefined" ? null : window;
  } catch {
    return null;
  }
}

function defaultNow(): number {
  return Date.now();
}

function defaultSetTimeout(callback: () => void, delayMs: number): unknown {
  return globalThis.setTimeout(callback, delayMs);
}

function defaultClearTimeout(handle: unknown): void {
  globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>);
}

function resolveOrigin(explicit: string | undefined, win: Window): string | null {
  if (explicit !== undefined) return normalizeOrigin(explicit);
  return normalizeOrigin(readProperty(readProperty(win, "location"), "origin"));
}

/**
 * Observe uncaught runtime errors, unhandled promise rejections, failed
 * resources and optional WebGL context loss. Reports contain bounded, sanitized
 * diagnostic messages and safe same-origin paths only; raw errors, rejection
 * objects and resource URLs are never retained or serialized. The observer
 * never suppresses the host page's own error handling.
 */
export function observePageErrors(
  onReport: (report: BrowserErrorReport) => void,
  options?: PageErrorObserverOptions,
): () => void {
  if (typeof onReport !== "function") return noop;

  let target: Window | null;
  let clock: () => number;
  let schedule: (callback: () => void, delayMs: number) => unknown;
  let cancel: (handle: unknown) => void;
  let explicitOrigin: string | undefined;
  try {
    const settings = options ?? {};
    target = settings.target ?? defaultTarget();
    clock = typeof settings.now === "function" ? settings.now : defaultNow;
    schedule = typeof settings.setTimeout === "function" ? settings.setTimeout : defaultSetTimeout;
    cancel =
      typeof settings.clearTimeout === "function" ? settings.clearTimeout : defaultClearTimeout;
    explicitOrigin =
      typeof settings.origin === "string" && settings.origin.length > 0
        ? settings.origin
        : undefined;
  } catch {
    return noop;
  }

  const win = target;
  if (!win) return noop;
  const browser: Window = win;
  const safeOrigin = resolveOrigin(explicitOrigin, browser);

  let disposed = false;
  let reports = 0;
  let fallbackClock = 0;
  const buckets = new Map<string, ErrorBucket>();
  const registered: RegisteredListener[] = [];

  const nowMs = (): number => {
    try {
      const value = clock();
      if (typeof value === "number" && Number.isFinite(value)) return value;
    } catch {
      // Fall through to the local monotonic fallback.
    }
    fallbackClock += 1;
    return fallbackClock;
  };

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const bucket of buckets.values()) {
      if (!bucket.hasTimer) continue;
      const pending = bucket.timer;
      bucket.timer = undefined;
      bucket.hasTimer = false;
      try {
        cancel(pending);
      } catch {
        // A host timer failure must not escape into the page.
      }
    }
    buckets.clear();
    for (const entry of registered) {
      try {
        browser.removeEventListener(entry.type, entry.listener, true);
      } catch {
        // Cleanup remains safe even if a host surface throws.
      }
    }
    registered.length = 0;
  }

  function signatureOf(report: BrowserErrorPayload): string {
    return JSON.stringify([
      report.kind,
      report.message,
      report.frames.map((frame) => [frame.path, frame.line, frame.column]),
    ]);
  }

  function emit(signature: string, bucket: ErrorBucket): void {
    if (disposed) return;
    bucket.hasTimer = false;
    bucket.timer = undefined;
    if (buckets.get(signature) !== bucket) return;
    buckets.delete(signature);
    reports += 1;
    const report: BrowserErrorReport = {
      kind: bucket.report.kind,
      message: bucket.report.message,
      frames: bucket.report.frames.map((frame) => ({ ...frame })),
      occurrences: Math.min(MAX_OCCURRENCES, Math.max(1, bucket.report.occurrences)),
    };
    try {
      onReport(report);
    } catch {
      // Analytics observers are best-effort and must not affect the host.
    }
    if (reports >= MAX_REPORTS) dispose();
  }

  function record(payload: BrowserErrorPayload): void {
    if (disposed || reports >= MAX_REPORTS) return;
    const signature = signatureOf(payload);
    const pending = buckets.get(signature);
    if (pending) {
      pending.report.occurrences = Math.min(MAX_OCCURRENCES, pending.report.occurrences + 1);
      return;
    }
    if (buckets.size >= MAX_REPORTS) return;

    const startedAt = nowMs();
    const bucket: ErrorBucket = {
      report: {
        kind: payload.kind,
        message: payload.message,
        frames: payload.frames.map((frame) => ({ ...frame })),
        occurrences: 1,
      },
      timer: undefined,
      hasTimer: false,
    };
    buckets.set(signature, bucket);
    const delay = Math.max(0, COALESCE_MS - Math.max(0, nowMs() - startedAt));
    try {
      bucket.timer = schedule(() => {
        if (disposed) return;
        if (buckets.get(signature) !== bucket) return;
        emit(signature, bucket);
      }, delay);
      bucket.hasTimer = true;
    } catch {
      bucket.timer = undefined;
      bucket.hasTimer = false;
      buckets.delete(signature);
    }
  }

  const onError = (event: Event): void => {
    if (disposed) return;
    try {
      const eventTarget = readProperty(event, "target");
      const resource = resourceTarget(eventTarget, safeOrigin);
      if (resource) {
        record({
          kind: "resource_load",
          message: summary("Resource failed to load", resource.tag),
          frames: resource.path ? [{ path: resource.path, line: 0, column: 0 }] : [],
        });
        return;
      }
      if (eventTarget !== null && eventTarget !== undefined && eventTarget !== browser) return;
      const error = readProperty(event, "error");
      const errorMessage = readProperty(error, "message");
      const eventMessage = readProperty(event, "message");
      const rawMessage =
        typeof errorMessage === "string" && errorMessage.length > 0
          ? errorMessage
          : typeof eventMessage === "string"
            ? eventMessage
            : null;
      record({
        kind: "runtime_exception",
        message: diagnosticMessage("Runtime exception", safeErrorName(error), rawMessage),
        frames: runtimeFrames(event, safeOrigin),
      });
    } catch {
      // Malformed or hostile events are ignored.
    }
  };

  const onUnhandledRejection = (event: Event): void => {
    if (disposed) return;
    try {
      const reason = readProperty(event, "reason");
      const name = safeErrorName(reason);
      let rawMessage: string | null = null;
      if (typeof reason === "string") {
        rawMessage = reason;
      } else if (name) {
        const message = readProperty(reason, "message");
        if (typeof message === "string" && message.length > 0) rawMessage = message;
      }
      record({
        kind: "unhandled_rejection",
        message: diagnosticMessage("Unhandled rejection", name, rawMessage),
        frames: stackFrames(reason, safeOrigin),
      });
    } catch {
      // Rejection bodies are never inspected beyond known, guarded properties.
    }
  };

  const onWebglContextLost = (): void => {
    if (disposed) return;
    record({
      kind: "webgl_context_lost",
      message: "WebGL context lost",
      frames: [],
    });
  };

  const listen = (type: string, listener: EventListener): void => {
    try {
      browser.addEventListener(type, listener, true);
      registered.push({ type, listener });
    } catch {
      // Unsupported event types and hostile targets are skipped quietly.
    }
  };

  listen("error", onError);
  listen("unhandledrejection", onUnhandledRejection);
  listen("webglcontextlost", onWebglContextLost);
  if (registered.length === 0) return noop;

  return dispose;
}
